import { IPaginatedData } from 'models/shared.models'
import {
  Payment,
  IPaymentOrderRequest,
  IBillingInformation,
} from 'models/payments'
import {
  compareSortByMultipleKeys,
  formatDateToSpanish,
} from 'lib/helpers/shared.helpers'
import HTTPClient, { httpErrorParser } from 'lib/helpers/HTTPClient'
import {
  ICancelRecurrentPaymentRequest,
  IGetPaymentsHistoryResponse,
  IRequestPlanUpdateRequest,
  TPaymentInvoiceFileTypeRequest,
} from './payments.models'
import { fileTypeParamsMap } from './payments.utils'
import { IPaginatedQueryRequest } from 'services/Shared/shared.models'

const route = process.env.REACT_APP_APIURL || '/api/'

export const getBillingInformation = async (
  selectedWorkspaceId: number
): Promise<IBillingInformation> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${selectedWorkspaceId}/payment`
    )
    const data: IBillingInformation = await petition.json()
    return {
      ...data,
      startPeriod: formatDateToSpanish(data.startPeriod, 'DD/MMM YYYY'),
      endPeriod: formatDateToSpanish(data.endPeriod, 'DD/MMM YYYY'),
      plans: data.plans.map(plan => ({
        ...plan,
        id: plan.idPlan,
        dateLimit: formatDateToSpanish(plan.dateLimit, 'DD/MMM YYYY'),
      })),
    }
  } catch (err) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const requestPlanUpdate = async (
  params: IRequestPlanUpdateRequest
): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    await client.post(`${route}/plans/change/notification`, params)
  } catch (err) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const deleteRecurrentPayment = async ({
  selectedWorkspaceId,
  planId,
}: ICancelRecurrentPaymentRequest): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    await client.delete(
      `${route}/workspaces/${selectedWorkspaceId}/payment/plan/${planId}/recurrent`
    )
  } catch (err) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const createPaymentOrder = async (
  selectedWorkspaceId: number,
  requestBody: IPaymentOrderRequest
): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    await client.post(
      `${route}/workspaces/${selectedWorkspaceId}/payment`,
      requestBody
    )
  } catch (err) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getPaymentsHistory = async ({
  activeWorkspace,
  page,
  pageSize,
}: IPaginatedQueryRequest<unknown>): Promise<IPaginatedData<Payment>> => {
  const client = HTTPClient.getClient()
  try {
    let url = `${route}/workspaces/${activeWorkspace}/payment/record?page=${page + 1}&size=${pageSize}`

    const petition = await client.get(url)
    const data: IGetPaymentsHistoryResponse = await petition.json()
    const paymentHistory = data.record

    const list: Payment[] = paymentHistory
      .sort((a, b) => compareSortByMultipleKeys(a, b, ['createdAt'], ['desc']))
      .map(payment => ({
        ...payment,
        id: payment.idOrder,
        payDay: payment.payDay ? payment.payDay : '---',
      }))

    return {
      list,
      total: data.totalElements,
    }
  } catch (err) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getPaymentInvoice = async (
  selectedWorkspaceId: number,
  payment: Payment,
  fileType: TPaymentInvoiceFileTypeRequest
): Promise<string> => {
  const client = HTTPClient.getClient()
  try {
    const fileResponse = await client.get(
      `${route}/workspaces/${selectedWorkspaceId}/cfdis/${payment.idInvoice ?? payment.uuid}/download/${fileTypeParamsMap[fileType]}`
    )
    const fileData = await fileResponse.text()
    const formattedData = `data:application/${fileType};base64,${fileData}`

    return formattedData
  } catch (err) {
    const error = await httpErrorParser(err)
    throw error
  }
}
